<template>
  <div class="tl">
    <Breadcrumbs main="Рейтинг" title="Рейтинг команд"/>

    <div class="container-fluid tl-container">

      <!-- ================== ФИЛЬТРЫ И ДЕЙСТВИЯ ================== -->
      <div class="tl-toolbar">
        <div class="tl-toolbar__main">
          <div class="tl-search" :class="{ 'is-busy': isLoadingRatingForTeams }">
            <i class="fa fa-search tl-search__icon"></i>
            <input ref="search"
                   type="search"
                   class="tl-search__input"
                   v-model="searchModel"
                   placeholder="Поиск по названию команды…"
                   @keydown.esc.prevent="clearSearch"/>
            <button v-if="searchModel" type="button" class="tl-search__clear" @click="clearSearch">
              <i class="fa fa-times"></i>
            </button>
            <kbd v-else class="tl-search__kbd">/</kbd>
          </div>

          <!-- дивизион: раньше пять одинаковых кнопок без выделения выбранной -->
          <div class="tl-seg rt-seg" role="group" aria-label="Дивизион">
            <button type="button" class="tl-seg__btn rt-seg__btn" :class="{ 'is-on': !activeDivision }"
                    @click="selectDivision(null)">
              Все
            </button>
            <button v-for="division in newDivisionsList" :key="'div' + division.id"
                    type="button" class="tl-seg__btn rt-seg__btn"
                    :class="{ 'is-on': isActiveDivision(division) }"
                    @click="selectDivision(division)">
              {{ division.name.ru }}
            </button>
          </div>

          <div class="tl-spacer"></div>

          <button type="button" class="tl-btn-ghost"
                  :disabled="!activeDivision"
                  v-b-tooltip.hover.bottom="activeDivision ? 'Скачать топ-20 дивизиона' : 'Сначала выберите дивизион'"
                  @click="downloadHandler">
            <i class="fa fa-download"></i> Топ 20
          </button>

          <button type="button" class="tl-btn-primary"
                  :disabled="isLoadingRatingActualization"
                  @click="showActualizeModal = true">
            <i class="fa" :class="isLoadingRatingActualization ? 'fa-spinner rt-spin' : 'fa-refresh'"></i>
            {{ isLoadingRatingActualization ? 'Идёт актуализация…' : 'Актуализировать' }}
          </button>
        </div>

        <div class="rt-meta">
          <span v-if="actualizedRatingTime">
            Рейтинг актуализирован: <b>{{ formatDateTime(actualizedRatingTime) }}</b>
          </span>
          <span v-if="activeDivision" class="rt-meta__chip">
            {{ activeDivision.name.ru }}
            <button type="button" @click="selectDivision(null)"><i class="fa fa-times"></i></button>
          </span>
          <span v-if="searchApplied" class="rt-meta__chip">
            Поиск: «{{ searchApplied }}»
            <button type="button" @click="clearSearch"><i class="fa fa-times"></i></button>
          </span>
        </div>
      </div>

      <!-- ================== СПИСОК ================== -->
      <div class="tl-panel">

        <div v-if="isLoadingRatingForTeams" class="tl-skeleton">
          <div v-for="n in 8" :key="'sk' + n" class="tl-skeleton__row">
            <span class="tl-sk tl-sk--sm"></span>
            <span class="tl-sk tl-sk--wide"></span>
            <span class="tl-sk"></span>
            <span class="tl-sk"></span>
            <span class="tl-sk tl-sk--sm"></span>
          </div>
        </div>

        <div v-else-if="!rows.length" class="tl-empty">
          <div class="tl-empty__art"><i class="fa fa-line-chart"></i></div>
          <div class="tl-empty__title">
            {{ searchApplied || activeDivision ? 'Ничего не нашлось' : 'Рейтинга пока нет' }}
          </div>
          <div class="tl-empty__text">
            {{ searchApplied || activeDivision
              ? 'Попробуйте изменить поиск или выбрать другой дивизион.'
              : 'Команды появятся здесь после актуализации рейтинга.' }}
          </div>
          <div v-if="searchApplied || activeDivision" class="tl-empty__actions">
            <button type="button" class="tl-btn-ghost" @click="resetFilters">Сбросить фильтры</button>
          </div>
        </div>

        <div v-else class="tl-table-wrap">
          <b-table class="tl-table rt-table"
                   responsive
                   hover
                   borderless
                   show-empty
                   :items="rows"
                   :fields="fields"
                   @row-clicked="openHistory">
            <template #cell(place)="{ index }">
              <span class="rt-place" :class="placeClass(index)">{{ placeNumber(index) }}</span>
            </template>

            <template #cell(name)="{ item }">
              <div class="rt-team">
                <router-link class="rt-team__name"
                             :to="{ name: 'team_ratings_info', params: { id: item.id } }"
                             @click.native.stop>{{ item.name }}</router-link>
                <button type="button" class="tl-id" @click.stop="copyId(item.id)"
                        v-b-tooltip.hover.right="'Скопировать ID команды'">
                  <span>#{{ item.id }}</span>
                  <i class="fa fa-copy"></i>
                </button>
              </div>
            </template>

            <template #cell(rating)="{ item }">
              <span class="rt-rating">{{ formatRating(item.rating) }}</span>
            </template>

            <template #cell(country)="{ item }">
              <span v-if="item.country && item.country.name">{{ item.country.name }}</span>
              <span v-else class="rt-dim">—</span>
            </template>

            <template #cell(tournament_count)="{ item }">
              <span class="rt-count">{{ item.tournament_count || 0 }}</span>
            </template>

            <template #cell(tournament_last_date)="{ item }">
              <span :class="{ 'rt-dim': !item.tournament_last_date }">
                {{ formatDateTime(item.tournament_last_date) }}
              </span>
            </template>

            <template #cell(created_at)="{ item }">
              <span class="rt-dim">{{ formatDateTime(item.created_at) }}</span>
            </template>

            <template #cell(action)="{ item }">
              <router-link class="tl-icon-btn rt-open"
                           :to="{ name: 'team_ratings_info', params: { id: item.id } }"
                           @click.native.stop
                           v-b-tooltip.hover.left="'История начислений'">
                <i class="fa fa-angle-right"></i>
              </router-link>
            </template>
          </b-table>
        </div>

        <div v-if="!isLoadingRatingForTeams && rows.length" class="tl-foot">
          <div class="tl-foot__info">
            Показано <b>{{ rangeFrom }}–{{ rangeTo }}</b> из <b>{{ total || 0 }}</b>
          </div>
          <b-pagination class="tl-pagination my-0"
                        v-model="currentPageModel"
                        :total-rows="total"
                        :per-page="perPage"
                        :limit="7"
                        @change="handlePageChange"/>
        </div>
      </div>
    </div>

    <ModalWrapper title="Вы уверены, что хотите актуализировать данные?"
                  name="ConfirmActualization"
                  @submit="submitForm"
                  @close-modal="() => showActualizeModal = false"
                  v-model="showActualizeModal"/>
  </div>
</template>

<script>
import { RatingAdapterMixin } from "../../mixins/Rating/RatingAdapterMixin";
import { ModalAdapterMixin } from "@/mixins/modal/ModalAdapterMixin";
import { PaginationHelperMixin } from "@/mixins/Helpers/PaginationHelperMixin";
import { DivisionsAdapterMixin } from "@/mixins/Divisions/DivisionsAdapterMixin";
import api from "@/api/api";
import Vue from "vue";
import lodash from "lodash";
import ModalWrapper from "@/components/modals/index.vue";

export default {
  name: "ListTeamsRating",
  components: {ModalWrapper},
  mixins: [
    RatingAdapterMixin,
    ModalAdapterMixin,
    PaginationHelperMixin,
    DivisionsAdapterMixin,
  ],
  created() {
    (async () => {
      try {
        this.unsetDevisionIdFromQuery()
        this.isLoadingRatingForTeams = true;
        this.recordPageToStorage(this.currentPage, this.pageKey);
        await this.grabTeamsRating({
          pageNumber: this.getCurrentPageNumber(this.pageKey),
        });
        
        await this.grabNewDivisions({ slug: "pubg-mobile" });
        await this.getDivisionsSlug();
        
        // Получение даты актуализации рейтинга
        const actualizedTimeForTournamentsRating = api.rating
          .actualizedTime()
          .then((response) => {
            this.actualizedRatingTime =
              response.data.data.lastActualizeDatetime || "";
          });

        // Получение доступных игр
        const gameList = api.games.index().then((gameList) => {

          this.gameId = gameList.data.games.find(
            (game) => game.slug === "pubg-mobile"
          ).id;
        });

        await Promise.all([actualizedTimeForTournamentsRating, gameList]);
      } finally {
        this.isLoadingRatingForTeams = false;
      }
    })();
  },
  data() {
    return {
      debouncedSearch: lodash.debounce(function () {
        this.getFilteredTeamsRating();
      }, 600),
      pageKey: "ratingPageNumber",
      fields: [
        { key: "place", label: "#", sortable: false, thClass: "rt-th--place" },
        { key: "name", label: "Команда", sortable: true },
        { key: "rating", label: "Рейтинг", sortable: true, thClass: "rt-th--num" },
        { key: "country", label: "Страна", sortable: true },
        { key: "tournament_count", label: "Турниров", sortable: true, thClass: "rt-th--num" },
        { key: "tournament_last_date", label: "Последний турнир", sortable: true },
        { key: "created_at", label: "Создана", sortable: true },
        { key: "action", label: "", sortable: false, thClass: "tl-th--act", tdClass: "tl-td--act" },
      ],
      perPage: 10,
      striped: true,
      bordered: true,
      divisionsSlug: [],
      gameId: null,
      actualizedRatingTime: "",
      isLoadingRatingActualization: false,
      showActualizeModal: false,
    };
  },
  computed: {
    rows() {
      return this.teamsRating || [];
    },

    /** Выбранный дивизион: раньше активный фильтр никак не подсвечивался. */
    activeDivision() {
      const slug = this.selectedDivisionSlug;
      const queryId = this.$route.query.divisionId;
      return (this.newDivisionsList || []).find((division) => {
        return (slug && division.slug === slug)
            || (!slug && queryId && String(division.id) === String(queryId));
      }) || null;
    },

    searchApplied() {
      return (this.searchForTeamsRatingModel || '').trim();
    },

    searchModel: {
      get() { return this.searchForTeamsRatingModel },
      set(value) {
        this.searchForTeamsRatingModel = value;
        this.debouncedSearch();
      }
    },

    currentPageModel: {
      get() { return Number(this.currentPage) || 1 },
      set(value) { this.currentPage = value }
    },

    rangeFrom() {
      if (!this.rows.length) return 0;
      return (this.currentPageModel - 1) * this.perPage + 1;
    },

    rangeTo() {
      return (this.currentPageModel - 1) * this.perPage + this.rows.length;
    },
  },
  mounted() {
    window.addEventListener('keydown', this.focusSearch);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.focusSearch);
  },
  methods: {
    /** Номер в списке: с учётом страницы, чтобы на второй странице шло 11, 12… */
    placeNumber(index) {
      return (this.currentPageModel - 1) * this.perPage + index + 1;
    },

    placeClass(index) {
      const place = this.placeNumber(index);
      if (place === 1) return 'rt-place--gold';
      if (place === 2) return 'rt-place--silver';
      if (place === 3) return 'rt-place--bronze';
      return '';
    },

    formatRating(value) {
      return Number(value || 0).toFixed(2);
    },

    formatDateTime(value) {
      if (!value) return '—';
      const date = new Date(String(value).replace(' ', 'T'));
      if (isNaN(date.getTime())) return String(value);
      const pad = (n) => String(n).padStart(2, '0');
      return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },

    isActiveDivision(division) {
      return Boolean(this.activeDivision && this.activeDivision.id === division.id);
    },

    selectDivision(division) {
      this.devisionHandler(division || {});
    },

    openHistory(item) {
      this.$router.push({ name: 'team_ratings_info', params: { id: item.id } });
    },

    copyId(id) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(String(id)).then(() => {
          this.$toasted.success('ID команды скопирован', {position: 'top-right', duration: 1500});
        }).catch(() => {
        });
      }
    },

    clearSearch() {
      this.searchForTeamsRatingModel = '';
      this.getFilteredTeamsRating();
    },

    resetFilters() {
      this.searchForTeamsRatingModel = '';
      this.selectDivision(null);
    },

    focusSearch(event) {
      if (event.key !== '/' || ['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target.tagName)) return;
      event.preventDefault();
      if (this.$refs.search) this.$refs.search.focus();
    },

    async submitForm() {
      try {
        this.isLoadingRatingActualization = true;
        await api.rating.actualize();
        Vue.toasted.success("Успешно актуализирован", {
          position: "top-right",
          duration: 3000,
        });
      } catch (error) {
        Vue.toasted.error(
          error.response.data.message || "Не удалось актуализировать",
          {
            position: "top-right",
            duration: 3000,
          }
        );
      } finally {
        this.isLoadingRatingActualization = false;
      }
    },
    downloadHandler() {
      const vidisionId = this.$route.query.divisionId;

      if (vidisionId) {
        const loadingToast = this.$toasted.info("Подготовка файла к загрузке...", { duration: null });
        
        api.divisions
        .download(vidisionId)
        .then((response) => {
          // Убираем уведомление о загрузке
          loadingToast.goAway(0);

          // Создаем объект Blob из данных ответа
          const blob = new Blob([response.data], {
            type: response.headers["content-type"],
          });

          // Создаем ссылку на этот Blob и "эмулируем" клик для загрузки
          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = "Топ 20 команд.xlsx";
          link.click();

          // Очищаем память, удаляя объект Blob
          URL.revokeObjectURL(link.href);
        })
        .catch((error) => {
          // Убираем уведомление о загрузке в случае ошибки
          loadingToast.goAway(0);

          // Обработка ошибки и вывод уведомления
          console.error("Ошибка при загрузке документа:", error);
          this.$toasted.error(
            error.response.data.message || "Ошибка при загрузке",
            { duration: 2000 }
          );
        });
      } else {

        this.$toasted.info("Выберите дивизион!", { duration: 2000 });
      }
      
    },
    setDevisionIdToQuery(id) {
      const query = { ...this.$route.query };

      if (id) {
        if (parseInt(query.divisionId) === id) return;
        query.divisionId = id;
      }

      this.$router.push({ query });
    },
    unsetDevisionIdFromQuery() {
      const query = { ...this.$route.query };

      if (query.divisionId) {
        delete query.divisionId;
        this.$router.push({ query });
      }
    },
    devisionHandler(division) {
      if (division.slug) {
        this.getTeamsByDivision(division.slug);
      } else {
        this.getTeamsByDivision();
      }

      if (division.id) {
        this.setDevisionIdToQuery(division.id);
      } else {
        this.unsetDevisionIdFromQuery();
      }
    },
    getDivisionsSlug() {
      this.divisionsSlug = this.newDivisionsList.map(
        (division) => division.slug
      );
    },
    handlePageChange(pageNumber) {
      this.recordPageToStorage(pageNumber, this.pageKey);
      this.grabTeamsRating({
        pageNumber: pageNumber,
        ...(this.searchForTeamsRatingModel && {
          search: this.searchForTeamsRatingModel,
        }),
        ...(this.divisionsSlug.includes(this.selectedDivisionSlug) && {
          division: this.selectedDivisionSlug,
        }),
      });
    },
    getFilteredTeamsRating() {
      this.isLoadingRatingForTeams = true;
      this.grabTeamsRating({
        search: this.searchForTeamsRatingModel,
        ...(this.divisionsSlug.includes(this.selectedDivisionSlug) && {
          division: this.selectedDivisionSlug,
        }),
      }).then(() => {
        this.isLoadingRatingForTeams = false;
      });
    },
  },
};
</script>

<style scoped lang="scss">
@import "~@/assets/scss/admin-list.scss";
@import "~@/assets/scss/rating-list.scss";

.tl { @include rating-list-parts; }
</style>
